import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Trash2, Plus, Phone, Mail, FileText, Calendar as CalIcon,
  MessageCircle, Send, UserCircle2, Pencil, Check, X, ChevronDown, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";

const QUICK_TEMPLATES = [
  { label: "Appeler le client", icon: Phone },
  { label: "Envoyer la facture", icon: Mail },
  { label: "Préparer la carte grise", icon: FileText },
  { label: "Confirmer la date de départ", icon: CalIcon },
];

function fmtDue(d) {
  if (!d) return "";
  try {
    const [y, m, dd] = d.split("-");
    return `${dd}/${m}/${y}`;
  } catch {
    return d;
  }
}

function fmtDateTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" }) +
      " " + d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

/** Fil de discussion (réponses) d'une tâche */
function TaskThread({ task, onUpdated }) {
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const reps = task.reponses || [];

  const send = async (e) => {
    e.preventDefault();
    const t = reply.trim();
    if (!t) return;
    setSending(true);
    try {
      const { data } = await api.post(`/taches/${task.id}/reponses`, { text: t });
      setReply("");
      onUpdated(data);
    } catch {
      toast.error("Erreur lors de l'envoi de la réponse");
    } finally {
      setSending(false);
    }
  };

  const removeReply = async (rid) => {
    try {
      const { data } = await api.delete(`/taches/${task.id}/reponses/${rid}`);
      onUpdated(data);
    } catch {
      toast.error("Erreur lors de la suppression");
    }
  };

  return (
    <div className="mt-2 ml-1 border-l-2 border-klein/30 pl-3 space-y-2" data-testid={`task-thread-${task.id}`}>
      {reps.map((r) => (
        <div key={r.id} className="group flex items-start gap-2" data-testid={`task-reply-${r.id}`}>
          <UserCircle2 size={14} className="mt-0.5 text-klein shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-[11px] text-muted-foreground">
              <span className="font-semibold text-foreground">{r.author}</span>
              {" · "}{fmtDateTime(r.created_at)}
            </div>
            <div className="text-sm whitespace-pre-wrap">{r.text}</div>
          </div>
          <button
            type="button"
            onClick={() => removeReply(r.id)}
            data-testid={`task-reply-delete-${r.id}`}
            className="opacity-0 group-hover:opacity-100 p-0.5 text-muted-foreground hover:text-rose-600 transition-opacity"
            aria-label="Supprimer la réponse"
          >
            <Trash2 size={12} />
          </button>
        </div>
      ))}
      <form onSubmit={send} className="flex gap-1.5">
        <Input
          data-testid={`task-reply-input-${task.id}`}
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          placeholder="Répondre… (ex: client appelé, passe samedi)"
          className="rounded-sm h-8 text-sm flex-1"
        />
        <Button
          type="submit"
          size="sm"
          disabled={sending || !reply.trim()}
          data-testid={`task-reply-send-${task.id}`}
          className="rounded-sm bg-klein hover:bg-klein/90 text-white h-8 px-3"
        >
          <Send size={13} />
        </Button>
      </form>
    </div>
  );
}

/** Une tâche : checkbox, texte, assignation, édition, fil de discussion */
function TaskItem({ task, members, showVenteLink, onToggle, onRemove, onUpdated }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(task.text);
  const [editDue, setEditDue] = useState(task.due_date || "");
  const nbReps = (task.reponses || []).length;

  const reassign = async (val) => {
    try {
      const { data } = await api.put(`/taches/${task.id}`, { assigned_to: val });
      onUpdated(data);
      toast.success(val ? "Tâche assignée" : "Assignation retirée");
    } catch {
      toast.error("Erreur d'assignation");
    }
  };

  const saveEdit = async () => {
    const t = editText.trim();
    if (!t) return;
    try {
      const { data } = await api.put(`/taches/${task.id}`, { text: t, due_date: editDue || "" });
      onUpdated(data);
      setEditing(false);
      toast.success("Tâche modifiée");
    } catch {
      toast.error("Erreur lors de la modification");
    }
  };

  return (
    <li data-testid={`task-${task.id}`} className={`py-2.5 ${task.done ? "opacity-60" : ""}`}>
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          checked={task.done}
          onChange={(e) => onToggle(task.id, e.target.checked)}
          data-testid={`task-check-${task.id}`}
          className="mt-1 h-4 w-4 accent-klein cursor-pointer"
        />
        <div className="flex-1 min-w-0">
          {editing ? (
            <div className="flex flex-col sm:flex-row gap-1.5">
              <Input
                data-testid={`task-edit-input-${task.id}`}
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") saveEdit(); if (e.key === "Escape") setEditing(false); }}
                className="rounded-sm h-8 text-sm flex-1"
                autoFocus
              />
              <Input
                type="date"
                value={editDue}
                onChange={(e) => setEditDue(e.target.value)}
                className="rounded-sm h-8 text-sm sm:w-36"
              />
              <div className="flex gap-1">
                <button
                  type="button"
                  onClick={saveEdit}
                  data-testid={`task-edit-save-${task.id}`}
                  className="p-1.5 bg-klein text-white rounded-sm hover:bg-klein/90"
                  aria-label="Enregistrer"
                >
                  <Check size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => { setEditing(false); setEditText(task.text); setEditDue(task.due_date || ""); }}
                  data-testid={`task-edit-cancel-${task.id}`}
                  className="p-1.5 border border-border rounded-sm hover:bg-slate-100"
                  aria-label="Annuler"
                >
                  <X size={14} />
                </button>
              </div>
            </div>
          ) : (
            <div className={`text-sm ${task.done ? "line-through text-muted-foreground" : ""}`}>
              {task.text}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground mt-0.5">
            {task.due_date && <span data-testid={`task-due-${task.id}`}>📅 {fmtDue(task.due_date)}</span>}
            {task.author && <span>par {task.author}</span>}
            {task.assigned_to_name && (
              <span
                data-testid={`task-assignee-${task.id}`}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-klein/10 text-klein rounded-sm font-semibold"
              >
                <UserCircle2 size={11} /> {task.assigned_to_name}
              </span>
            )}
            {showVenteLink && task.vente_id && (
              <Link to={`/ventes/${task.vente_id}`} className="text-klein hover:underline">
                Voir la vente →
              </Link>
            )}
            <button
              type="button"
              onClick={() => setOpen((o) => !o)}
              data-testid={`task-thread-toggle-${task.id}`}
              className={`inline-flex items-center gap-1 hover:text-klein transition-colors ${nbReps > 0 ? "text-klein font-semibold" : ""}`}
            >
              <MessageCircle size={12} />
              {nbReps > 0 ? `${nbReps} réponse${nbReps > 1 ? "s" : ""}` : "Répondre"}
            </button>
          </div>
          {open && <TaskThread task={task} onUpdated={onUpdated} />}
        </div>
        <button
          type="button"
          onClick={() => { setEditing((e) => !e); setEditText(task.text); setEditDue(task.due_date || ""); }}
          data-testid={`task-edit-${task.id}`}
          className="p-1 text-muted-foreground hover:text-klein hover:bg-klein/5 rounded-sm"
          aria-label="Modifier"
          title="Modifier la tâche"
        >
          <Pencil size={14} />
        </button>
        <select
          value={task.assigned_to || ""}
          onChange={(e) => reassign(e.target.value)}
          data-testid={`task-assign-${task.id}`}
          title="Assigner à"
          className="h-7 max-w-[110px] text-[11px] bg-slate-50 border border-border rounded-sm px-1 text-muted-foreground cursor-pointer hover:border-klein focus:outline-none"
        >
          <option value="">— Assigner</option>
          {members.map((m) => (
            <option key={m.id} value={m.id}>{m.name}</option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => onRemove(task.id)}
          data-testid={`task-delete-${task.id}`}
          className="p-1 text-muted-foreground hover:text-rose-600 hover:bg-rose-50 rounded-sm"
          aria-label="Supprimer"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </li>
  );
}

/**
 * Internal tasks / reminders.
 * Pass `venteId` to attach tasks to a specific sale; omit to manage all tasks of the company.
 */
export default function TaskList({ venteId, title = "Tâches internes", showVenteLink = false }) {
  const [tasks, setTasks] = useState([]);
  const [members, setMembers] = useState([]);
  const [text, setText] = useState("");
  const [due, setDue] = useState("");
  const [assignTo, setAssignTo] = useState("");
  const [loading, setLoading] = useState(false);
  const [showDone, setShowDone] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = venteId ? { vente_id: venteId } : {};
      const { data } = await api.get("/taches", { params });
      setTasks(data);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [venteId]);

  useEffect(() => {
    api.get("/taches/membres").then(({ data }) => setMembers(data)).catch(() => {});
  }, []);

  const add = async (txt = text) => {
    const t = (txt || "").trim();
    if (!t) return;
    try {
      const payload = { text: t };
      if (venteId) payload.vente_id = venteId;
      if (due) payload.due_date = due;
      if (assignTo) payload.assigned_to = assignTo;
      await api.post("/taches", payload);
      setText("");
      setDue("");
      setAssignTo("");
      load();
    } catch (e) {
      toast.error("Erreur lors de l'ajout");
    }
  };

  const toggle = async (id, done) => {
    await api.put(`/taches/${id}`, { done });
    load();
  };

  const remove = async (id) => {
    await api.delete(`/taches/${id}`);
    load();
  };

  const patchTask = (updated) => {
    setTasks((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
  };

  const pendingTasks = tasks.filter((t) => !t.done);
  const doneTasks = tasks.filter((t) => t.done);

  return (
    <section className="bg-card border border-border rounded-sm p-5" data-testid="tasklist">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground font-semibold">
            Rappels commercial
          </div>
          <h3 className="font-display font-bold text-lg">{title}</h3>
        </div>
        {!loading && tasks.length > 0 && (
          <div className="text-xs text-muted-foreground" data-testid="tasklist-count">
            {tasks.filter((t) => !t.done).length} en attente · {tasks.filter((t) => t.done).length} faite(s)
          </div>
        )}
      </div>

      {/* Quick templates */}
      <div className="flex flex-wrap gap-1.5 mb-3">
        {QUICK_TEMPLATES.map((q) => {
          const Icon = q.icon;
          return (
            <button
              key={q.label}
              type="button"
              data-testid={`task-template-${q.label.replace(/\s+/g, "-")}`}
              onClick={() => add(q.label)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] bg-slate-100 hover:bg-klein hover:text-white rounded-sm transition-colors"
            >
              <Icon size={11} /> {q.label}
            </button>
          );
        })}
      </div>

      {/* Add form */}
      <form
        onSubmit={(e) => { e.preventDefault(); add(); }}
        className="flex flex-col sm:flex-row gap-2 mb-4"
      >
        <Input
          data-testid="task-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Ajouter une tâche (ex: rappeler le 06/05 pour la carte grise)"
          className="rounded-sm h-9 flex-1"
        />
        <Input
          data-testid="task-due"
          type="date"
          value={due}
          onChange={(e) => setDue(e.target.value)}
          className="rounded-sm h-9 sm:w-40"
        />
        <select
          data-testid="task-assign-select"
          value={assignTo}
          onChange={(e) => setAssignTo(e.target.value)}
          className="h-9 sm:w-44 text-sm bg-background border border-border rounded-sm px-2 cursor-pointer focus:outline-none focus:border-klein"
        >
          <option value="">Assigner à… (optionnel)</option>
          {members.map((m) => (
            <option key={m.id} value={m.id}>{m.name}</option>
          ))}
        </select>
        <Button
          type="submit"
          data-testid="task-add"
          className="rounded-sm bg-klein hover:bg-klein/90 text-white h-9"
        >
          <Plus size={14} className="mr-1" /> Ajouter
        </Button>
      </form>

      {/* List */}
      {loading ? (
        <div className="text-sm text-muted-foreground py-4">Chargement…</div>
      ) : tasks.length === 0 ? (
        <div className="text-sm text-muted-foreground py-4 text-center bg-slate-50 rounded-sm">
          Aucune tâche. Ajoutez-en une au-dessus 👆
        </div>
      ) : (
        <>
          {pendingTasks.length === 0 ? (
            <div className="text-sm text-muted-foreground py-3 text-center bg-slate-50 rounded-sm">
              🎉 Aucune tâche en attente
            </div>
          ) : (
            <ul className="divide-y divide-border" data-testid="tasklist-items">
              {pendingTasks.map((t) => (
                <TaskItem
                  key={t.id}
                  task={t}
                  members={members}
                  showVenteLink={showVenteLink}
                  onToggle={toggle}
                  onRemove={remove}
                  onUpdated={patchTask}
                />
              ))}
            </ul>
          )}

          {/* Section Terminées */}
          {doneTasks.length > 0 && (
            <div className="mt-4 pt-3 border-t border-border">
              <button
                type="button"
                onClick={() => setShowDone((s) => !s)}
                data-testid="tasklist-done-toggle"
                className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-klein transition-colors"
              >
                {showDone ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                Terminées ({doneTasks.length})
              </button>
              {showDone && (
                <ul className="divide-y divide-border mt-1" data-testid="tasklist-done-items">
                  {doneTasks.map((t) => (
                    <TaskItem
                      key={t.id}
                      task={t}
                      members={members}
                      showVenteLink={showVenteLink}
                      onToggle={toggle}
                      onRemove={remove}
                      onUpdated={patchTask}
                    />
                  ))}
                </ul>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}
